import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getPayments, updatePayment, getCreditControl, addPaymentNote } from '@/lib/api';
import { CreditCard, Loader2, Check, AlertTriangle, Clock, ExternalLink, Mail, ChevronDown, ChevronUp, Send, MessageSquare } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const statusConfig = {
  paid: { bg: 'bg-[#E8EDE5]', text: 'text-[#3A4035]', icon: Check, label: 'Paid' },
  pending: { bg: 'bg-[#F4EFE6]', text: 'text-[#7A6849]', icon: Clock, label: 'Pending' },
  overdue: { bg: 'bg-[#F5E6E6]', text: 'text-[#8A3D3D]', icon: AlertTriangle, label: 'Overdue' },
};

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [creditItems, setCreditItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedId, setExpandedId] = useState(null);
  const [confirmPay, setConfirmPay] = useState(null);
  const [view, setView] = useState(searchParams.get('view') || 'all');
  const filter = searchParams.get('status') || 'all';

  const setFilter = (v) => {
    if (v === 'all') setSearchParams({});
    else setSearchParams({ status: v });
  };

  useEffect(() => {
    Promise.all([getPayments(), getCreditControl()])
      .then(([all, credit]) => { setPayments(all); setCreditItems(credit); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleMarkPaid = async (paymentId) => {
    try {
      const now = new Date().toISOString().split('T')[0];
      const updated = await updatePayment(paymentId, { status: 'paid', paid_date: now, reference: `MANUAL-${Date.now().toString(36).toUpperCase()}` });
      setPayments(prev => prev.map(p => p.payment_id === paymentId ? updated : p));
      setCreditItems(prev => prev.filter(p => p.payment_id !== paymentId));
      setConfirmPay(null);
    } catch (err) { console.error(err); }
  };

  const requestMarkPaid = (pay) => { setConfirmPay(pay); };

  const filtered = filter === 'all' ? payments : payments.filter(p => p.status === filter);
  const showCredit = view === 'credit';
  const overdueCount = creditItems.filter(p => p.status === 'overdue').length;
  const pendingCount = creditItems.filter(p => p.status === 'pending').length;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Payments</h1>
          <p className="text-sm text-[#5C5C55] mt-1">{payments.length} total records</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex bg-[#F2F0EB] rounded-xl p-1">
            <button onClick={() => setView('all')} className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${!showCredit ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#8A8B85]'}`} data-testid="view-all">All</button>
            <button onClick={() => setView('credit')} className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 ${showCredit ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#8A8B85]'}`} data-testid="view-credit">
              Credit Control
              {overdueCount > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F5E6E6] text-[#8A3D3D] font-bold">{overdueCount}</span>}
            </button>
          </div>
          {!showCredit && (
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full sm:w-44 bg-white rounded-xl border-[#E5E2DC] h-10" data-testid="payment-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Summary — contextual */}
      {showCredit ? (
        <CreditControlSummary payments={payments} creditItems={creditItems} />
      ) : (
        <div className="flex gap-3 mb-6 overflow-x-auto pb-2">
          <div className="shrink-0 bg-[#E8EDE5] rounded-2xl px-5 py-3">
            <p className="text-xs font-semibold text-[#3A4035]">Paid</p>
            <p className="text-lg font-heading font-medium text-[#3A4035]">{payments.filter(p => p.status === 'paid').length}</p>
          </div>
          <div className="shrink-0 bg-[#F4EFE6] rounded-2xl px-5 py-3">
            <p className="text-xs font-semibold text-[#7A6849]">Pending</p>
            <p className="text-lg font-heading font-medium text-[#7A6849]">{pendingCount}</p>
          </div>
          <div className="shrink-0 bg-[#F5E6E6] rounded-2xl px-5 py-3">
            <p className="text-xs font-semibold text-[#8A3D3D]">Overdue</p>
            <p className="text-lg font-heading font-medium text-[#8A3D3D]">{overdueCount}</p>
          </div>
        </div>
      )}

      {/* ── CREDIT CONTROL VIEW ── */}
      {showCredit && (
        <div className="space-y-2" data-testid="credit-control-list">
          {creditItems.length === 0 && (
            <div className="text-center py-16">
              <Check className="w-10 h-10 text-[#D8DBD3] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-[#8A8B85]">No outstanding payments</p>
            </div>
          )}
          {creditItems.map((pay, i) => {
            const cfg = statusConfig[pay.status] || statusConfig.pending;
            const Icon = cfg.icon;
            const isExpanded = expandedId === pay.payment_id;
            const isQB = pay.source === 'quickbooks';
            return (
              <div key={pay.payment_id}
                className={`bg-white rounded-2xl border shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden ${pay.days_overdue > 14 ? 'border-[#F0D4D4]' : pay.days_overdue > 0 ? 'border-[#EDE4C8]' : 'border-[#E5E2DC]'}`}
                data-testid={`credit-item-${pay.payment_id}`}>
                <button onClick={() => setExpandedId(isExpanded ? null : pay.payment_id)}
                  className="w-full p-4 sm:p-5 flex items-center gap-4 text-left">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cfg.bg}`}>
                    <Icon className={`w-4 h-4 ${cfg.text}`} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-[#1A1A1A]">{pay.occupier_name || pay.occupier_id}</p>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${isQB ? 'bg-[#E8EDF2] text-[#4A6080]' : 'bg-[#F2F0EB] text-[#8A8B85]'}`}>
                        {isQB ? 'QuickBooks' : 'Manual'}
                      </span>
                    </div>
                    <p className="text-xs text-[#8A8B85]">£{pay.amount?.toFixed(0)} · Due {pay.due_date}
                      {pay.days_overdue > 0 && <span className="text-[#8A3D3D] font-medium"> · {pay.days_overdue}d overdue</span>}
                    </p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold capitalize shrink-0 ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-[#8A8B85] shrink-0" strokeWidth={1.5} /> : <ChevronDown className="w-4 h-4 text-[#8A8B85] shrink-0" strokeWidth={1.5} />}
                </button>
                {isExpanded && (
                  <div className="px-4 sm:px-5 pb-4 border-t border-[#F2F0EB] pt-3 space-y-3">
                    <div className="flex flex-wrap gap-3 text-xs text-[#5C5C55]">
                      {pay.occupier_email && <span>{pay.occupier_email}</span>}
                      {pay.occupier_phone && <span>{pay.occupier_phone}</span>}
                      {pay.reference && <span>Ref: {pay.reference}</span>}
                    </div>
                    {/* QB link placeholder */}
                    {pay.qb_invoice_id && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-[#8A8B85]">QB Invoice: {pay.qb_invoice_id}</span>
                        {pay.qb_invoice_url && (
                          <a href={pay.qb_invoice_url} target="_blank" rel="noopener noreferrer" className="text-[#3A4035] font-medium flex items-center gap-1 hover:underline">
                            View in QuickBooks <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                          </a>
                        )}
                      </div>
                    )}
                    {!pay.qb_invoice_id && (
                      <p className="text-[10px] text-[#8A8B85] italic">No QuickBooks invoice linked — tracked manually</p>
                    )}
                    {/* Actions */}
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => requestMarkPaid(pay)}
                        className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors flex items-center gap-1.5"
                        data-testid={`credit-markpaid-${pay.payment_id}`}>
                        <Check className="w-3 h-3" strokeWidth={2} />Mark paid
                      </button>
                      <button className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#5C5C55] border border-[#E5E2DC] hover:bg-[#F2F0EB] transition-colors flex items-center gap-1.5"
                        data-testid={`credit-reminder-${pay.payment_id}`}
                        onClick={() => { if (pay.occupier_email) window.location.href = `mailto:${pay.occupier_email}?subject=Payment reminder — Go Freestyle Studios&body=Hi ${pay.occupier_name},%0D%0A%0D%0AThis is a friendly reminder that your studio payment of £${pay.amount?.toFixed(0)} was due on ${pay.due_date}.%0D%0A%0D%0APlease let us know if you have any questions.%0D%0A%0D%0ABest regards,%0D%0AGo Freestyle Studios`; }}>
                        <Mail className="w-3 h-3" strokeWidth={1.5} />Email reminder
                      </button>
                      {pay.occupier_phone && (
                        <button className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#25D366] border border-[#D4EDDA] hover:bg-[#F0FAF3] transition-colors flex items-center gap-1.5"
                          data-testid={`credit-wa-reminder-${pay.payment_id}`}
                          onClick={() => { const ph = pay.occupier_phone.replace(/\s/g, '').replace(/^0/, '44'); window.open(`https://wa.me/${ph}?text=${encodeURIComponent(`Hi ${pay.occupier_name},\n\nThis is a friendly reminder that your studio payment of £${pay.amount?.toFixed(0)} was due on ${pay.due_date}.\n\nPlease let us know if you have any questions.\n\nThanks,\nGo Freestyle Studios`)}`, '_blank'); }}>
                          <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          WhatsApp
                        </button>
                      )}
                      {pay.qb_invoice_url ? (
                        <a href={pay.qb_invoice_url} target="_blank" rel="noopener noreferrer"
                          className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#5C5C55] border border-[#E5E2DC] hover:bg-[#F2F0EB] transition-colors flex items-center gap-1.5">
                          <ExternalLink className="w-3 h-3" strokeWidth={1.5} />View invoice
                        </a>
                      ) : (
                        <span className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#F2F0EB] text-[#8A8B85] flex items-center gap-1.5">
                          <ExternalLink className="w-3 h-3" strokeWidth={1.5} />View invoice
                        </span>
                      )}
                    </div>
                    {/* Activity / Notes */}
                    <PaymentNotes paymentId={pay.payment_id} activity={pay.activity || []} onUpdated={(updated) => setCreditItems(prev => prev.map(p => p.payment_id === updated.payment_id ? {...p, ...updated} : p))} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── ALL PAYMENTS VIEW ── */}
      {!showCredit && (
        <div className="space-y-2">
          {filtered.map((pay, i) => {
            const cfg = statusConfig[pay.status] || statusConfig.pending;
            const Icon = cfg.icon;
            const isQB = !!pay.qb_invoice_id;
            return (
              <div key={pay.payment_id}
                className={`bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-4 sm:p-5 flex items-center gap-4 animate-fade-in stagger-${(i % 6) + 1}`}
                data-testid={`payment-row-${pay.payment_id}`}>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cfg.bg}`}>
                  <Icon className={`w-4 h-4 ${cfg.text}`} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A]">£{pay.amount?.toFixed(0)}</p>
                  <p className="text-xs text-[#8A8B85]">Due {pay.due_date}</p>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  <p className="text-xs text-[#5C5C55]">{pay.occupier_name || pay.occupier_id}</p>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${isQB ? 'bg-[#E8EDF2] text-[#4A6080]' : 'bg-[#F2F0EB] text-[#8A8B85]'}`}>
                    {isQB ? 'QB' : 'Manual'}
                  </span>
                </div>
                {pay.reference && <p className="hidden sm:block text-xs text-[#8A8B85]">{pay.reference}</p>}
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold capitalize shrink-0 ${cfg.bg} ${cfg.text}`}>{pay.status}</span>
                {pay.status !== 'paid' && (
                  <button onClick={() => requestMarkPaid(pay)}
                    className="text-xs px-3 py-1.5 rounded-full bg-[#3A4035] text-white font-medium hover:bg-[#2B3027] transition-colors shrink-0"
                    data-testid={`mark-paid-${pay.payment_id}`}>
                    Mark paid
                  </button>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-16">
              <CreditCard className="w-10 h-10 text-[#D8DBD3] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-[#8A8B85]">No payments found</p>
            </div>
          )}
        </div>
      )}

      {/* Confirm Mark Paid Dialog */}
      {confirmPay && (
        <Dialog open={!!confirmPay} onOpenChange={() => setConfirmPay(null)}>
          <DialogContent className="sm:max-w-sm bg-white rounded-2xl border-[#E5E2DC]" data-testid="confirm-paid-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">Confirm Payment</DialogTitle>
              <DialogDescription className="text-xs text-[#5C5C55]">Mark this payment as received?</DialogDescription>
            </DialogHeader>
            <div className="bg-[#FAF9F6] rounded-xl p-4 border border-[#F2F0EB]">
              <p className="text-sm font-medium text-[#1A1A1A]">{confirmPay.occupier_name || confirmPay.occupier_id}</p>
              <p className="text-xs text-[#8A8B85] mt-1">£{confirmPay.amount?.toFixed(0)} · Due {confirmPay.due_date}</p>
            </div>
            <div className="flex gap-3 pt-1">
              <Button variant="outline" onClick={() => setConfirmPay(null)} className="flex-1 rounded-full h-10 border-[#E5E2DC]">Cancel</Button>
              <Button onClick={() => handleMarkPaid(confirmPay.payment_id)} className="flex-1 bg-[#3A4035] text-white rounded-full h-10 hover:bg-[#2B3027]" data-testid="confirm-paid-btn">
                <Check className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Mark Paid
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


function CreditControlSummary({ payments, creditItems }) {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const thisMonthPayments = payments.filter(p => p.due_date?.startsWith(currentMonth));
  const dueThisMonth = thisMonthPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const collectedThisMonth = thisMonthPayments.filter(p => p.status === 'paid').reduce((s, p) => s + (p.amount || 0), 0);
  const outstanding = dueThisMonth - collectedThisMonth;
  const overdueTotal = creditItems.filter(p => p.status === 'overdue').reduce((s, p) => s + (p.amount || 0), 0);
  const oldestOverdue = creditItems.reduce((max, p) => Math.max(max, p.days_overdue || 0), 0);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="credit-summary">
      <div className="bg-white rounded-2xl border border-[#E5E2DC] p-4">
        <p className="text-[10px] text-[#8A8B85] uppercase tracking-wider font-semibold mb-1">Due this month</p>
        <p className="text-xl font-heading font-medium text-[#1A1A1A]">£{dueThisMonth.toLocaleString()}</p>
      </div>
      <div className="bg-[#F0F2EF] rounded-2xl border border-[#D8DBD3] p-4">
        <p className="text-[10px] text-[#3A4035] uppercase tracking-wider font-semibold mb-1">Collected</p>
        <p className="text-xl font-heading font-medium text-[#3A4035]">£{collectedThisMonth.toLocaleString()}</p>
      </div>
      <div className={`rounded-2xl border p-4 ${outstanding > 0 ? 'bg-[#FDFAF0] border-[#EDE4C8]' : 'bg-white border-[#E5E2DC]'}`}>
        <p className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${outstanding > 0 ? 'text-[#8A7230]' : 'text-[#8A8B85]'}`}>Outstanding</p>
        <p className={`text-xl font-heading font-medium ${outstanding > 0 ? 'text-[#8A7230]' : 'text-[#1A1A1A]'}`}>£{outstanding.toLocaleString()}</p>
      </div>
      <div className={`rounded-2xl border p-4 ${overdueTotal > 0 ? 'bg-[#FDF2F2] border-[#F0D4D4]' : 'bg-white border-[#E5E2DC]'}`}>
        <p className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${overdueTotal > 0 ? 'text-[#B33A3A]' : 'text-[#8A8B85]'}`}>Overdue total</p>
        <p className={`text-xl font-heading font-medium ${overdueTotal > 0 ? 'text-[#B33A3A]' : 'text-[#1A1A1A]'}`}>£{overdueTotal.toLocaleString()}</p>
      </div>
      <div className={`rounded-2xl border p-4 ${oldestOverdue > 14 ? 'bg-[#FDF2F2] border-[#F0D4D4]' : oldestOverdue > 0 ? 'bg-[#FDFAF0] border-[#EDE4C8]' : 'bg-white border-[#E5E2DC]'}`}>
        <p className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${oldestOverdue > 14 ? 'text-[#B33A3A]' : oldestOverdue > 0 ? 'text-[#8A7230]' : 'text-[#8A8B85]'}`}>Oldest overdue</p>
        <p className={`text-xl font-heading font-medium ${oldestOverdue > 14 ? 'text-[#B33A3A]' : oldestOverdue > 0 ? 'text-[#8A7230]' : 'text-[#1A1A1A]'}`}>{oldestOverdue > 0 ? `${oldestOverdue}d` : '—'}</p>
      </div>
    </div>
  );
}


function PaymentNotes({ paymentId, activity, onUpdated }) {
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const notes = activity || [];

  const handleAdd = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      const updated = await addPaymentNote(paymentId, noteText.trim());
      onUpdated(updated);
      setNoteText('');
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  return (
    <div className="pt-2 mt-2 border-t border-[#F2F0EB]" data-testid={`payment-notes-${paymentId}`}>
      <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-2 flex items-center gap-1.5">
        <MessageSquare className="w-3 h-3" strokeWidth={1.5} />Activity ({notes.length})
      </p>
      {notes.length > 0 && (
        <div className="space-y-1.5 mb-2 max-h-32 overflow-y-auto">
          {[...notes].reverse().map(n => (
            <div key={n.note_id} className="bg-[#FAF9F6] rounded-lg p-2.5 border border-[#F2F0EB]">
              <p className="text-[11px] text-[#1A1A1A] leading-relaxed">{n.text}</p>
              <p className="text-[9px] text-[#8A8B85] mt-1">{n.author} · {n.created_at ? new Date(n.created_at).toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}) + ', ' + new Date(n.created_at).toLocaleTimeString('en-GB', {hour:'2-digit',minute:'2-digit'}) : ''}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." rows={1}
          className="flex-1 bg-[#F2F0EB] border-transparent rounded-lg text-[11px] placeholder:text-[#8A8B85] resize-none py-2 min-h-0"
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAdd(); }} data-testid={`payment-note-input-${paymentId}`} />
        <button onClick={handleAdd} disabled={saving || !noteText.trim()}
          className="bg-[#3A4035] text-white rounded-lg px-2.5 h-auto self-end hover:bg-[#2B3027] shrink-0 disabled:opacity-40"
          data-testid={`payment-note-send-${paymentId}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" strokeWidth={1.5} />}
        </button>
      </div>
    </div>
  );
}
